import { useEffect, useState, useMemo } from 'react';
import { FilterBar } from './components/FilterBar';
import { useFilters } from './components/FilterContext';
import { transformDashboardData } from '../../utils/dashboardDataTransform';
import axiosInstance from '../../utils/axios.js';

const IDEAL_RANGES = {
  ph: { min: 6.5, max: 7.5 },
  organic_carbon: { min: 0.8, max: 2.0 },
  nitrogen: { min: 280, max: 560 },
  phosphorus: { min: 10, max: 25 },
  potassium: { min: 110, max: 280 },
  sulfur: { min: 10, max: 20 },
  zinc: { min: 0.5, max: 1.0 },
  iron: { min: 5.0, max: 10.0 },
  boron: { min: 0.1, max: 0.5 },
  copper: { min: 0.2, max: 0.8 },
  manganese: { min: 2.0, max: 5.0 },
};

export function SoilHealthExplorer() {
  const { filters } = useFilters();
  const [data, setData] = useState([]);
  const [selectedNutrient, setSelectedNutrient] = useState('organic_carbon');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get('/admin/dashboard-data');
      const transformedData = transformDashboardData(response.data.dashboardData || []);
      setData(transformedData);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.response?.data?.msg || 'Failed to load dashboard data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }

  const filteredData = useMemo(() => {
    if (!data.length) return [];
    
    return data.filter(item => {
      if (filters.villages && filters.villages.length > 0) {
        if (!filters.villages.includes(item.farmers?.village)) return false;
      }
      if (filters.previousCrop && filters.previousCrop.length > 0) {
        if (!filters.previousCrop.includes(item.previous_crop)) return false;
      }
      if (filters.farmSize && filters.farmSize.length > 0) {
        const size = parseFloat(item.farm_size);
        if (isNaN(size)) return false;
        
        const matchesSize = filters.farmSize.some(filterOption => {
          if (filterOption === '< 1 acre') return size < 1;
          if (filterOption === '1-2 acres') return size >= 1 && size <= 2;
          if (filterOption === '> 2 acres') return size > 2;
          return false;
        });

        if (!matchesSize) return false;
      }
      return true;
    });
  }, [data, filters]);

  const uniqueFarmersData = useMemo(() => {
    const map = new Map();
    
    filteredData.forEach(item => {
      const uniqueKey = item.farmer_id; 
      if (uniqueKey && !map.has(uniqueKey)) {
        map.set(uniqueKey, item); 
      }
    });
    
    return Array.from(map.values());
  }, [filteredData]);

  const nutrients = [
    { key: 'ph', label: 'pH', unit: '' },
    { key: 'organic_carbon', label: 'Organic Carbon', unit: '%' },
    { key: 'nitrogen', label: 'Nitrogen (N)', unit: 'kg/ha' },
    { key: 'phosphorus', label: 'Phosphorus (P)', unit: 'kg/ha' },
    { key: 'potassium', label: 'Potassium (K)', unit: 'kg/ha' },
    { key: 'sulfur', label: 'Sulfur (S)', unit: 'kg/ha' },
    { key: 'zinc', label: 'Zinc (Zn)', unit: 'ppm' },
    { key: 'iron', label: 'Iron (Fe)', unit: 'ppm' },
    { key: 'boron', label: 'Boron (B)', unit: 'ppm' },
    { key: 'copper', label: 'Copper (Cu)', unit: 'ppm' },
    { key: 'manganese', label: 'Manganese (Mn)', unit: 'ppm' },
  ];

  const selectedNutrientInfo = nutrients.find(n => n.key === selectedNutrient);
  const idealRange = IDEAL_RANGES[selectedNutrient];

  const values = uniqueFarmersData.map(d => d[selectedNutrient]).filter(v => v !== null && v !== undefined);
  const distribution = calculateDistribution(values, idealRange.min, idealRange.max);
  const villageDistribution = calculateVillageDistribution(uniqueFarmersData, selectedNutrient, idealRange.min, idealRange.max);

  if (loading) {
    return (
      <div className="p-8">
        <FilterBar data={data} />
        <div className="flex items-center justify-center h-64 text-gray-500">Loading data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <FilterBar data={data} />
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F5F5F5]">
      <FilterBar data={data} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="lg:col-span-1 bg-white rounded-lg shadow-sm p-4 sm:p-6 lg:sticky lg:top-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Select Nutrient</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-2">
              {nutrients.map(n => (
                <button
                  key={n.key}
                  onClick={() => setSelectedNutrient(n.key)}
                  className={`w-full text-left px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm transition-colors ${selectedNutrient === n.key
                    ? 'bg-[#2E7D32] text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  {n.label}
                </button>
              ))}
            </div>
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">Ideal Range</h4>
              <p className="text-xs sm:text-sm text-gray-700">
                {idealRange.min} - {idealRange.max} {selectedNutrientInfo.unit}
              </p>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-4 sm:space-y-6">
            <DistributionChart nutrient={selectedNutrientInfo.label} unit={selectedNutrientInfo.unit} data={distribution} idealRange={idealRange} />
            <VillageDistributionChart nutrient={selectedNutrientInfo.label} villages={villageDistribution} />
            <NutrientHeatmap data={uniqueFarmersData} />
          </div>
        </div>
      </div>
    </div>
  );
}

function calculateDistribution(values, min, max) {
  const low = values.filter(v => v < min).length;
  const medium = values.filter(v => v >= min && v <= max).length;
  const high = values.filter(v => v > max).length;
  return { low, medium, high };
}

function calculateVillageDistribution(data, nutrient, min, max) {
  const villages = [...new Set(data.map(d => d.farmers?.village))];

  return villages.map(village => {
    const values = data.filter(d => d.farmers?.village === village).map(d => d[nutrient]).filter(Boolean);
    const total = values.length;
    const low = values.filter(v => v < min).length;
    const medium = values.filter(v => v >= min && v <= max).length;
    const high = values.filter(v => v > max).length;

    return {
      village,
      low: total ? Math.round((low / total) * 100) : 0,
      medium: total ? Math.round((medium / total) * 100) : 0,
      high: total ? Math.round((high / total) * 100) : 0,
      lowCount: low,
    };
  }).sort((a, b) => b.lowCount - a.lowCount);
}

function DistributionChart({ nutrient, unit, data, idealRange }) {
  const total = data.low + data.medium + data.high;
  const colors = {
    low: '#D32F2F',
    medium: '#2E7D32',
    high: '#FFA000',
  };
  const labels = {
    low: 'Low',
    medium: 'Ideal',
    high: 'High',
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Distribution: {nutrient}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Distribution of soil samples with {nutrient} levels below, within, and above the recommended range.
        </p>
      </div>
      <div className="space-y-4">
        {['low', 'medium', 'high'].map(key => {
          const percent = total > 0 ? Math.round((data[key] / total) * 100) : 0;
          return (
            <div key={key}>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-700 font-medium">{labels[key]}</span>
                <span className="text-gray-600">{data[key]} ({percent}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8">
                <div
                  className="h-8 rounded-full flex items-center justify-center text-white text-sm font-medium transition-all"
                  style={{
                    width: `${percent}%`,
                    backgroundColor: colors[key],
                  }}
                >
                  {data[key] > 0 && `${data[key]}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VillageDistributionChart({ nutrient, villages }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {nutrient} Status by Village
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Percentage of soil samples in each village that are below, within, or above the recommended {nutrient} range.
        </p>
      </div>
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {villages.map(v => (
          <div key={v.village} className="space-y-1">
            <div className="text-sm text-gray-700 font-medium">{v.village}</div>
            <div className="flex h-8 rounded-lg overflow-hidden">
              {v.low > 0 && (
                <div
                  className="bg-[#D32F2F] flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${v.low}%` }}
                >
                  {v.low}%
                </div>
              )}
              {v.medium > 0 && (
                <div
                  className="bg-[#2E7D32] flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${v.medium}%` }}
                >
                  {v.medium}%
                </div>
              )}
              {v.high > 0 && (
                <div
                  className="bg-[#FFA000] flex items-center justify-center text-white text-xs font-medium"
                  style={{ width: `${v.high}%` }}
                >
                  {v.high}%
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NutrientHeatmap({ data }) {
  const villages = [...new Set(data.map(d => d.farmers?.village))].slice(0, 10);
  const nutrients = [
    { key: 'ph', label: 'pH' },
    { key: 'organic_carbon', label: 'OC' },
    { key: 'nitrogen', label: 'N' },
    { key: 'phosphorus', label: 'P' },
    { key: 'potassium', label: 'K' },
    { key: 'zinc', label: 'Zn' },
    { key: 'iron', label: 'Fe' },
  ];

  const getCellStatus = (village, nutrient) => {
    const villageData = data.filter(d => d.farmers?.village === village);
    if (villageData.length === 0) return null;

    const values = villageData.map(d => d[nutrient]).filter(v => v !== null && v !== undefined);
    if (values.length === 0) return null;

    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    const ideal = IDEAL_RANGES[nutrient];
    if (!ideal) return null;

    if (avg < ideal.min) return 'low';
    if (avg > ideal.max) return 'high';
    return 'ideal';
  };

  const getCellClass = (status) => {
    if (!status) return 'bg-gray-100';
    if (status === 'ideal') return 'bg-green-100';
    if (status === 'low') return 'bg-red-100';
    return 'bg-amber-100';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 overflow-x-auto">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Multi-Nutrient Overview by Village
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Summary of the average nutrient status across villages. ✓ indicates the nutrient is within the recommended range, ↓ indicates deficiency, and ↑ indicates excess.
        </p>
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="border border-gray-300 p-2 bg-gray-50 text-sm font-semibold text-gray-700 text-left">Village</th>
            {nutrients.map(n => (
              <th key={n.key} className="border border-gray-300 p-2 bg-gray-50 text-sm font-semibold text-gray-700 text-center">
                {n.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {villages.map(village => (
            <tr key={village}>
              <td className="border border-gray-300 p-2 text-sm text-gray-900">{village}</td>
              {nutrients.map(n => {
                const status = getCellStatus(village, n.key);
                return (
                  <td key={n.key} className={`border border-gray-300 p-2 text-sm text-center ${getCellClass(status)}`}>
                    {status ? (status === 'ideal' ? '✓' : status === 'low' ? '↓' : '↑') : '-'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}